import Loading from "@components/common/Loading";
import Modal from "@components/common/Modal";
import Notification from "@components/common/Notification";
import { TextInputDefault } from "@components/form/TextInput"
import { API_URL } from "@redux/api";
import axios, { AxiosResponse } from "axios";
import { useCallback, useEffect, useState } from "react"
import { NotificationType } from "react-app-env";
import { FiPlusCircle } from "react-icons/fi";
import { IoSearch } from "react-icons/io5";
import AddNewAdminForm from "./AddNewAdminForm";
import EditAdminForm from "./EditAdminForm";

export type AdminType = {
  _id?: string;
  name: string;
  phone: string;
  email: string;
  role: string;
  isActive: boolean;
};

const AdminCard = ({data, onClick}: {data:AdminType, onClick: () => void}) => {
  const {
    name,
    phone,
    email,
    role,
    isActive
  } = data;

  return (
    <div className="bg-white rounded-xl p-4">
      <table>
        <tbody>
          <tr>
            <td className="">
              <label>Nama</label>
            </td>
            <td className="px-3"><span>:</span></td>
            <td>
              <span className="capitalize font-semibold">{name}</span>
            </td>
          </tr>
          <tr>
            <td><label>Email</label></td>
            <td className="px-3"><span>:</span></td>
            <td><span className="font-semibold">{email}</span></td>
          </tr>
          <tr>
            <td><label>No. HP</label></td>
            <td className="px-3"><span>:</span></td>
            <td><span className="capitalize font-semibold">{phone ? phone : '-'}</span></td>
          </tr>
          <tr>
            <td><label>Jabatan</label></td>
            <td className="px-3"><span>:</span></td>
            <td><span className="capitalize font-semibold">{role}</span></td>
          </tr>
          <tr>
            <td><label>Status</label></td>
            <td className="px-3"><span>:</span></td>
            <td><span className={`capitalize font-semibold ${isActive ? "text-green-600" : "text-red-600"}`}>{isActive ? "Aktif" : "Non-aktif"}</span></td>
          </tr>
        </tbody>
      </table>
      <div className="mt-5">
        <button
          className="bg-orange-600 rounded-xl text-white w-full py-2"
          onClick={onClick}
        >
          Ubah data
        </button>
      </div>
    </div>
  )
};

const AdminList = () => {

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [notification, setNotification] = useState<NotificationType | null>(null);

  const [searchName, setSearchName] = useState<string>("");

  const [data, setData] = useState<AdminType[] | []>([]);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminType | null>(null);

  const [isOpenAddAdminModal, setIsOpenAddAdminModal] = useState<boolean>(false);
  const [isOpenEditAdminModal, setIsOpenEditAdminModal] = useState<boolean>(false);

  const fetchAdmins = useCallback(async({searchName}: {searchName: string}) => {
    setIsLoading(true);
    setNotification(null);

    const payload = { searchName };
    const response: AxiosResponse = await axios.post(`${API_URL}/admin/list`, 
      payload
    );

    const { status, data } = response;

    if(status === 200) {
      setData(data.data);
      setIsLoading(false);
    } else {
      setNotification({
        isError: true,
        title: "Error",
        text: data.message,
      });
      setIsLoading(false);
    };
  }, []);

  useEffect(() => {
    if(!searchName) fetchAdmins({searchName});
    return () => {};
  }, [searchName, fetchAdmins]);

  return (
    <div>
      <div className="mt-5">
        <Notification notification={notification} />
      </div>

      <div className="mt-8">
        <div className="flex items-end gap-3 justify-between">
          <TextInputDefault 
            name="searchName"
            label="Cari Admin"
            placeholder="Cari berdasarkan nama"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            error=""
            disabled={isLoading}
          />
          <button
            className="flex items-center gap-2 bg-orange-600 text-white px-6 py-3 rounded-xl"
            onClick={() => fetchAdmins({searchName})}
          >
            <IoSearch />
            Cari
          </button>
        </div>
      </div>

      <div className="mt-10">
        <button 
          className="w-full bg-sky-400 p-4 rounded-xl flex items-center justify-center gap-2 text-white"
          onClick={() => {
            setSelectedAdmin(null)
            setIsOpenAddAdminModal(true);
            setIsOpenEditAdminModal(false);
          }}
        >
          <FiPlusCircle />
          Tambah Admin Baru
        </button>
      </div>

      <div className="mt-8">
        {isLoading ? 
          <div className="mt-3">
            <Loading loading={isLoading} size={12} type="beat-loader" />
          </div>
          : data.length > 0 ? 
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mt-7">
              {data.map(item => {
                return (
                  <AdminCard
                    key={item._id}
                    data={item}
                    onClick={() => {
                      setSelectedAdmin(item)
                      setIsOpenEditAdminModal(true);
                      setIsOpenAddAdminModal(false);
                    }}
                  />
                )
              })}
            </div>
          : <div className="mt-3 h-52 flex items-center justify-center bg-white rounded-xl">
            <h2>Tidak ada data ditemukan</h2>
          </div>
        }
      </div>

      <Modal
        showCloseButton
        title="Form Tambah Admin"
        isOpen={isOpenAddAdminModal}
        onClose={() => setIsOpenAddAdminModal(false)}
      >
        <div className="border-t">
          <AddNewAdminForm 
            onClose={() => {
              fetchAdmins({searchName});
              setIsOpenAddAdminModal(false);
            }}
          />
        </div>
      </Modal>

      <Modal
        showCloseButton
        title="Form Edit Admin"
        isOpen={isOpenEditAdminModal}
        onClose={() => {
          setSelectedAdmin(null);
          setIsOpenEditAdminModal(false);
        }}
      >
        <div className="border-t">
          <EditAdminForm 
            data={selectedAdmin} 
            onClose={() => {
              fetchAdmins({searchName});
              setSelectedAdmin(null);
              setIsOpenEditAdminModal(false);
            }} 
          />
        </div>
      </Modal>
    </div>
  )
}

export default AdminList